import React, { useState, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Search, 
  Filter, 
  X, 
  Calendar, 
  User, 
  Tag,
  Clock,
  AlertCircle,
  CheckCircle,
  FileText,
  Target,
  SlidersHorizontal
} from 'lucide-react';
import { TaskStatus, TaskPriority, ProjectStatus, UserRole } from '@/types';

interface SearchFilters {
  query: string;
  type: 'all' | 'projects' | 'tasks' | 'users';
  status: string;
  priority: string;
  assignee: string;
  dateRange: string;
  tags: string[];
}

export const AdvancedSearch: React.FC = () => {
  const { projects, tasks } = useProject();
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    type: 'all',
    status: 'all',
    priority: 'all',
    assignee: 'all',
    dateRange: 'all',
    tags: []
  });
  const [showFilters, setShowFilters] = useState(false);

  // Mock users
  const mockUsers = [
    { id: '1', name: 'Admin User', email: 'admin@example.com', role: UserRole.ADMIN, avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', email: 'manager@example.com', role: UserRole.MANAGER, avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', email: 'collaborator@example.com', role: UserRole.COLLABORATOR, avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    tasks.forEach(task => {
      task.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags);
  }, [tasks]);

  // Filter and search logic
  const searchResults = useMemo(() => {
    let results: any[] = [];

    // Projects
    if (filters.type === 'all' || filters.type === 'projects') {
      let filteredProjects = projects;

      // Text search
      if (filters.query) {
        filteredProjects = filteredProjects.filter(project => 
          project.name.toLowerCase().includes(filters.query.toLowerCase()) ||
          project.description.toLowerCase().includes(filters.query.toLowerCase())
        );
      }

      // Status filter
      if (filters.status !== 'all') {
        filteredProjects = filteredProjects.filter(project => 
          project.status === filters.status
        );
      }

      // Date range filter
      if (filters.dateRange !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        if (filters.dateRange === 'week') {
          filterDate.setDate(now.getDate() - 7);
        } else if (filters.dateRange === 'month') {
          filterDate.setMonth(now.getMonth() - 1);
        } else if (filters.dateRange === 'quarter') {
          filterDate.setMonth(now.getMonth() - 3);
        }

        filteredProjects = filteredProjects.filter(project => 
          new Date(project.createdAt) >= filterDate
        );
      }

      results.push(...filteredProjects.map(project => ({
        ...project,
        type: 'project',
        searchScore: calculateSearchScore(project.name + ' ' + project.description, filters.query)
      })));
    }

    // Tasks
    if (filters.type === 'all' || filters.type === 'tasks') {
      let filteredTasks = tasks;

      // Text search
      if (filters.query) {
        filteredTasks = filteredTasks.filter(task => 
          task.title.toLowerCase().includes(filters.query.toLowerCase()) ||
          task.description.toLowerCase().includes(filters.query.toLowerCase())
        );
      }

      // Status filter
      if (filters.status !== 'all') {
        filteredTasks = filteredTasks.filter(task => 
          task.status === filters.status
        );
      }

      // Priority filter
      if (filters.priority !== 'all') {
        filteredTasks = filteredTasks.filter(task => 
          task.priority === filters.priority
        );
      }

      // Assignee filter
      if (filters.assignee !== 'all') {
        filteredTasks = filteredTasks.filter(task => 
          task.assignedTo === filters.assignee
        );
      }

      // Tags filter
      if (filters.tags.length > 0) {
        filteredTasks = filteredTasks.filter(task => 
          filters.tags.some(tag => task.tags.includes(tag))
        );
      }

      results.push(...filteredTasks.map(task => ({
        ...task,
        type: 'task',
        searchScore: calculateSearchScore(task.title + ' ' + task.description, filters.query)
      })));
    }

    // Users
    if (filters.type === 'all' || filters.type === 'users') {
      let filteredUsers = mockUsers;

      // Text search
      if (filters.query) {
        filteredUsers = filteredUsers.filter(user => 
          user.name.toLowerCase().includes(filters.query.toLowerCase()) ||
          user.email.toLowerCase().includes(filters.query.toLowerCase())
        );
      }

      results.push(...filteredUsers.map(user => ({
        ...user,
        type: 'user',
        searchScore: calculateSearchScore(user.name + ' ' + user.email, filters.query)
      })));
    }

    // Sort by search score and relevance
    return results.sort((a, b) => b.searchScore - a.searchScore);
  }, [projects, tasks, filters]);

  const calculateSearchScore = (text: string, query: string): number => {
    if (!query) return 1;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    
    if (lowerText.includes(lowerQuery)) {
      // Exact match gets higher score
      if (lowerText === lowerQuery) return 100;
      // Starts with query gets good score
      if (lowerText.startsWith(lowerQuery)) return 80;
      // Contains query gets medium score
      return 60;
    }
    
    return 0;
  };

  const updateFilter = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const addTag = (tag: string) => {
    if (!filters.tags.includes(tag)) {
      setFilters(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tag: string) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const clearFilters = () => {
    setFilters({
      query: '',
      type: 'all',
      status: 'all',
      priority: 'all',
      assignee: 'all',
      dateRange: 'all',
      tags: []
    });
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'project': return <Target className="w-4 h-4 text-purple-500" />;
      case 'task': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'user': return <User className="w-4 h-4 text-green-500" />;
      default: return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string, type: string) => {
    if (type === 'project') {
      switch (status) {
        case ProjectStatus.COMPLETED: return 'bg-green-100 text-green-800';
        case ProjectStatus.IN_PROGRESS: return 'bg-blue-100 text-blue-800';
        case ProjectStatus.ON_HOLD: return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    } else if (type === 'task') {
      switch (status) {
        case TaskStatus.DONE: return 'bg-green-100 text-green-800';
        case TaskStatus.IN_PROGRESS: return 'bg-blue-100 text-blue-800';
        case TaskStatus.REVIEW: return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    }
    return 'bg-gray-100 text-gray-800';
  };

  const activeFiltersCount = Object.values(filters).filter(value => 
    value !== 'all' && value !== '' && (Array.isArray(value) ? value.length > 0 : true)
  ).length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Busca Avançada</h1>
          <p className="text-gray-600 mt-1">Encontre projetos, tarefas e usuários</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            className="relative"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filtros
            {activeFiltersCount > 0 && (
              <Badge className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center p-0">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
          <Button variant="outline" onClick={clearFilters}>
            <X className="w-4 h-4 mr-2" />
            Limpar
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Pesquisar projetos, tarefas ou usuários..."
              value={filters.query}
              onChange={(e) => updateFilter('query', e.target.value)}
              className="pl-9 h-12 text-lg"
            />
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros Avançados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Tipo</label>
                <Select value={filters.type} onValueChange={(value) => updateFilter('type', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="projects">Projetos</SelectItem>
                    <SelectItem value="tasks">Tarefas</SelectItem>
                    <SelectItem value="users">Usuários</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Status</label>
                <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="todo">A Fazer</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="review">Em Revisão</SelectItem>
                    <SelectItem value="done">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Prioridade</label>
                <Select value={filters.priority} onValueChange={(value) => updateFilter('priority', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Responsável</label>
                <Select value={filters.assignee} onValueChange={(value) => updateFilter('assignee', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {mockUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tags Filter */}
            <div className="mt-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Tags</label>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <Button
                    key={tag}
                    variant={filters.tags.includes(tag) ? "default" : "outline"}
                    size="sm"
                    onClick={() => filters.tags.includes(tag) ? removeTag(tag) : addTag(tag)}
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Button>
                ))}
              </div>
            </div>

            {/* Active Filters */}
            {activeFiltersCount > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900">Filtros ativos:</span>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-1" />
                    Limpar todos
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {filters.tags.map(tag => (
                    <Badge key={tag} variant="secondary">
                      {tag}
                      <X className="w-3 h-3 ml-1 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Resultados da Busca</span>
            <Badge variant="secondary">{searchResults.length} resultados</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {searchResults.length > 0 ? (
              searchResults.map((result, index) => (
                <div key={`${result.type}-${result.id}`} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getResultIcon(result.type)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">
                            {result.type === 'project' ? result.name : 
                             result.type === 'task' ? result.title : 
                             result.name}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            {result.type === 'project' ? 'Projeto' : 
                             result.type === 'task' ? 'Tarefa' : 
                             'Usuário'}
                          </Badge>
                        </div>
                        
                        {result.status && (
                          <Badge variant="secondary" className={getStatusColor(result.status, result.type)}>
                            {result.status}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1">
                        {result.type === 'project' ? result.description : 
                         result.type === 'task' ? result.description : 
                         result.email}
                      </p>
                      
                      <div className="flex items-center space-x-4 mt-2">
                        {result.type === 'task' && result.assignedTo && (
                          <div className="flex items-center space-x-1">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {mockUsers.find(u => u.id === result.assignedTo)?.name}
                            </span>
                          </div>
                        )}
                        
                        {result.type === 'task' && result.endDate && (
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">
                              {new Date(result.endDate).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                        )}
                        
                        {result.type === 'task' && result.tags.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <Tag className="w-3 h-3 text-gray-400" />
                            <div className="flex space-x-1">
                              {result.tags.slice(0, 3).map((tag: string) => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                              {result.tags.length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{result.tags.length - 3}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Nenhum resultado encontrado</p>
                <p className="text-sm mt-1">Tente ajustar os filtros ou termo de busca</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};